import React from 'react'
import { Box, Typography } from '@mui/material'
import { 
  Datagrid, 
  List, 
  ListProps, 
  TextField, 
  FunctionField,
  SelectInput
} from 'react-admin'

import { Subscription, SubscriptionStatus } from 'api/subscriptions'
import CustomDateField from 'common/CustomDateField'
import CustomLinkField from 'common/CustomLinkField'
import CustomPagination from 'common/CustomPagination'
import { FilterExportToolbar } from 'common/FilertExportToolbar'
import { parseTimestamp } from 'utils/helpers'
import UserService from 'UserService'

const subscriptionFilters = [
  <SelectInput 
    source="status" 
    choices={[
      { id: SubscriptionStatus.ACTIVE, name: 'Active' },
      { id: SubscriptionStatus.INACTIVE, name: 'Inactive' },
      { id: SubscriptionStatus.TRIALING, name: 'Trialing' },
      { id: SubscriptionStatus.EXPIRED, name: 'Expired' }
    ]}
    alwaysOn 
    label="Status" 
    resettable 
  />
]

const getStatusColor = (status: SubscriptionStatus) => {
  switch (status) {
    case SubscriptionStatus.ACTIVE:
      return '#A8DDC2'
    case SubscriptionStatus.TRIALING:
      return '#FF9296'
    case SubscriptionStatus.INACTIVE:
      return '#F6D887'
    case SubscriptionStatus.EXPIRED:
      return '#FFCCCB'
    default:
      return '#F2F2F2'
  }
}

const getStatusTextColor = (status: SubscriptionStatus) => {
  switch (status) {
    case SubscriptionStatus.ACTIVE:
      return '#064A27'
    case SubscriptionStatus.TRIALING:
      return '#682527'
    case SubscriptionStatus.INACTIVE:
      return '#5D4609'
    case SubscriptionStatus.EXPIRED:
      return '#8B0000'
    default:
      return '#3B3B3B'
  }
}

const StatusField = ({ record }: { record?: Subscription }) => {
  if (!record) return null
  
  return (
    <Box
      component="span"
      sx={{
        backgroundColor: getStatusColor(record.status),
        color: getStatusTextColor(record.status),
        borderRadius: '3px',
        padding: '6px 12px',
        textTransform: 'uppercase',
        fontSize: '12px',
        fontWeight: 'bold'
      }}
    >
      {record.status}
    </Box>
  )
}

const SubscriptionList = (props: ListProps<Subscription>) => {
  const { timezone } = UserService.getTokenUserInfo()

  return (
    <Box margin="60px">
      <Typography variant="h4" component="h1" gutterBottom>
        Subscriptions
      </Typography>
      
      <List 
        {...props} 
        perPage={50} 
        pagination={<CustomPagination />}
        actions={<FilterExportToolbar filters={subscriptionFilters} showFilter showExport />}
        className="gray-skin"
      >
        <Datagrid bulkActionButtons={false}>
          <CustomLinkField 
            source="companyId" 
            label="Company" 
            basePath="companies" 
            show 
            targetFieldId="companyId" 
            sortable={false}
          />
          <TextField source="version" label="Version" sortable={false} />
          <FunctionField 
            source="status" 
            label="Status" 
            render={(record?: Subscription) => <StatusField record={record} />}
            sortable={false}
          />
          <CustomDateField source="startDate" label="Start Date" sortable={false} />
          <FunctionField 
            source="endDate" 
            label="End Date" 
            render={(record?: Subscription) => 
              record?.endDate ? (
                parseTimestamp(record.endDate, timezone, false)
              ) : (
                <Box sx={{ color: '#4caf50', fontWeight: 'bold' }}>
                  Endless ♾️
                </Box>
              )
            }
            sortable={false}
          />
          <FunctionField 
            source="createdAt" 
            label="Created At" 
            render={(record?: Subscription) => 
              record?.createdAt ? parseTimestamp(record.createdAt, timezone, true) : 'Unknown'
            }
            sortable={false}
          />
        </Datagrid>
      </List>
    </Box>
  )
}

export default SubscriptionList 